<template>
  <div class="rs-tags">
    <label v-if="label">{{ label }}</label>
    <div :class="getClasses" class="rs-tags__container form-control">
      <input
        v-model="search"
        type="text"
        class="rs-tags__input form-control"
        :placeholder="placeholder"
        :disabled="disabled"
        @keydown="onKeyDown"
      />

      <RsListGroup v-if="search">
        <RsListGroupItem
          v-if="canCreateTag"
          clickable
          :class="{ 'active-tag-list-item': selectedTagIndex === -1 }"
          @click="onAddNewTag"
        >
          Add `{{ search }}` tag.
        </RsListGroupItem>
        <RsListGroupItem
          v-for="(tag, index) in availableTags"
          :key="`${tag.name}-${index}`"
          clickable
          :class="{ 'active-tag-list-item': selectedTagIndex === index }"
          :v-show="category ? tag.isCategory : ''"
          @click="onChooseTag(tag)"
        >
          {{ tag.name }}
        </RsListGroupItem>
      </RsListGroup>

      <div
        v-if="input.length > 0"
        class="rs-tags__chips"
      >
        <span
          v-for="option in input"
          :key="option.id"
          class="rs-tags__chip"
        >
          {{ option.name }}
          <button
            type="button"
            class="rs-tags__chip-remove"
            aria-label="Remove"
            @click="onRemoveTag(option)"
          >
            &times;
          </button>
        </span>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue';
import { IObjectsHasTags, ITags } from '@llws/lift-entity-interfaces';

export default defineComponent({
  name: 'RsTags',

  props: {
    /**
     * Set input tag id.
     */
    id: {
      type: [String, Number],
      default: '',
    },
    label: {
      type: String,
      default: '',
    },
    /**
     * Add input attritubes
     */
    attrs: {
      type: Object as PropType<Record<string, unknown>>,
      default: () => ({}),
    },
    /**
     * Value
     */
    value: {
      type: Array as PropType<ITags[]>,
      default: () => [],
    },
    /**
     * Separator characters
     */
    separator: {
      type: String,
      default: '',
    },
    /**
     * Input Placeholder
     */
    placeholder: {
      type: String,
      default: 'Add tag',
    },
    category: {
      type: Boolean,
      default: false,
    },
    /**
     * Disable enter to add tag
     */
    noAddOnEnter: {
      type: Boolean,
      default: false,
    },
    /**
     * Enable backspace to delete tag
     */
    removeOnDelete: {
      type: Boolean,
      default: true,
    },
    /**
     * Disable to type
     */
    disabled: {
      type: Boolean,
      default: false,
    },
    /**
     * Tag variants: primary | secondary | success | warning | danger | info | light
     */
    variant: {
      type: String as PropType<string>,
      default: 'primary',
      validator: (value: string) => [
        'primary',
        'secondary',
        'success',
        'warning',
        'danger',
        'info',
        'light',
      ].includes(value),
    },
    /**
     * Input tag size: sm | md | lg
     */
    size: {
      type: String,
      default: 'md',
    },
    /**
     * Show the pilled tag
     */
    pills: {
      type: Boolean,
      default: false,
    },
    /**
     * Validation state
     */
    state: {
      type: Boolean,
      default: true,
    },
    tags: {
      type: Array as PropType<ITags[]>,
      default: () => [],
    },
    isObjectHasTags: {
      type: Boolean,
      default: true,
    },
  },

  data() {
    return {
      input: (this as any).mappingTags(),
      search: '' as string,
      selectedTagIndex: -1 as number,
    };
  },

  computed: {
    getClasses(): Record<string, string|boolean> {
      return {
        'rs-tags--primary': this.variant === 'primary',
        'rs-tags--secondary': this.variant === 'secondary',
        'rs-tags--success': this.variant === 'success',
        'rs-tags--warning': this.variant === 'warning',
        'rs-tags--error': this.variant === 'error',
        'rs-tags--info': this.variant === 'info',
        'rs-tags--light': this.variant === 'light',
        'rs-tags--dark': this.variant === 'dark',
        'rs-tags--white': this.variant === 'white',
        'rs-tags--black': this.variant === 'black',
        'rs-tags--active': !this.disabled,
        'rs-tags--disabled': this.disabled,
      };
    },

    availableTags(): ITags[] {
      if (!this.tags || this.tags.length === 0 || !this.search) return [];

      return this.tags.filter((tag: ITags) => {
        const index = this.input.findIndex((tagItem: ITags) => tagItem.name === tag.name || tagItem.id === tag.id);
        return tag.name.toLowerCase().includes(this.search.toLowerCase()) && index === -1;
      });
    },

    canCreateTag(): boolean {
      if (!this.search) return false;

      const valueIndex = this.input.findIndex((tag: ITags) => tag.name.toLowerCase() === this.search.toLowerCase());
      if (valueIndex > -1) return false;

      if (!this.availableTags || this.availableTags.length === 0) return true;
      return this.tags.findIndex((tag: ITags) => tag.name.toLowerCase() === this.search.toLowerCase()) === -1;
    },
  },

  watch: {
    value(): void {
      this.input = this.mappingTags();
    },
    availableTags(): void {
      if (this.canCreateTag) {
        this.selectedTagIndex = -1;
      } else if (this.availableTags && this.availableTags.length > 0 && this.selectedTagIndex === -1 && !this.canCreateTag) {
        this.selectedTagIndex = 0;
      }
    },
  },

  methods: {
    onAddNewTag(): void {
      if (this.selectedTagIndex === -1) {
        if (this.canCreateTag) {
          const newTag = {
            name: this.search,
            clientId: this.$rsGlobals.$currentUser.currentClientId,
            isCategory: this.category ? 1 : 0,
            siteId: this.$route.params.siteId,
            created: new Date(),
            createdUserId: this.$rsGlobals.$currentUser.userId,
            modifiedUserId: this.$rsGlobals.$currentUser.userId,
          };
          this.$emit('on-add-tag', newTag.name);
          this.input.push(newTag);
        }
        this.search = '';
      } else {
        this.onChooseTag(this.availableTags[this.selectedTagIndex]);
      }
    },
    clickOutside(): void {
      this.search = '';
    },
    onChooseTag(tag: ITags): void {
      this.$emit('on-choose-tag', tag);
      this.input.push(tag);
      this.search = '';
    },
    onRemoveTag(option: ITags): void {
      this.$emit('on-remove-tag', option);
      this.input = this.input.filter((tagItem: ITags) => tagItem.name !== option.name);
    },
    changeTagSelector(step: number): void {
      if (step === -1) {
        if (this.selectedTagIndex === -1) return;
        if (this.selectedTagIndex === 0 && !this.canCreateTag) return;
      } else if (this.selectedTagIndex === this.availableTags.length - 1) return;
      this.selectedTagIndex += step;
    },
    onKeyDown(event: KeyboardEvent) {
      switch (event.key) {
        case 'Enter':
          event.preventDefault();
          this.onAddNewTag();
          break;
        case 'ArrowDown':
          event.preventDefault();
          this.changeTagSelector(1);
          break;
        case 'ArrowUp':
          event.preventDefault();
          this.changeTagSelector(-1);
          break;
        default:
      }
    },
    mappingTags(): ITags[] {
      if (this.isObjectHasTags && this.value) {
        return this.value.map((tag) => ({ ...(tag as unknown as IObjectsHasTags).tag })) as ITags[] || [];
      }
      return [...this.value];
    },
  },
});
</script>

<style lang="scss" scoped>
  @import '@/styles/_variables.scss';
  .rs-tags {
    color: inherit;
    margin-bottom: .5rem;

    &__container {
      height: auto;
      padding: 0.5rem;
      display: flex;
      flex-direction: column;
      gap: 0.5rem;
      cursor: text;
    }

    &__input {
      width: 100%;
    }

    &__chips {
      display: flex;
      flex-wrap: wrap;
      gap: 0.375rem;
    }

    &__chip {
      display: inline-flex;
      align-items: center;
      gap: 0.25rem;
      background-color: #cfe2ff;
      color: #084298;
      border-radius: 0.25rem;
      padding: 0.125rem 0.5rem;
      font-size: 0.875rem;
      line-height: 1.5;
      max-width: 100%;
    }

    &__chip-remove {
      background: none;
      border: none;
      color: inherit;
      cursor: pointer;
      padding: 0;
      font-size: 1.1rem;
      line-height: 1;
      opacity: 0.7;
      flex-shrink: 0;

      &:hover {
        opacity: 1;
      }
    }

    .rs-list-group {
      position: absolute;
      z-index: 180;
    }

    .rs-list-group-item {
      padding: .5rem;
    }
  }
</style>
